//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip, Card, CardContent } from '@mui/material'
import { Link } from 'react-router-dom'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import StarIcon from '@mui/icons-material/Star'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { steps, products, whoItsFor } from '../../common/NetworkProvenApproachContent'
import { COLOR_PRIMARY, NAVBAR_BG, DARK_800, BG_LIGHT, SOLUTIONS_HERO_START, SOLUTIONS_HERO_MID, SECTION_DARK, SECTION_LIGHT } from '../../theme'
import { WaveDivider } from '../../components/WaveDivider'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { BookDemoModal } from '../../components/BookDemoModal'
import { proofPoints } from '../../common/StatsContent'
import { REVIEWS_URL } from '../../common/common'
import { shouldntWont } from '../../common/ProblemContent'
// import { quotes } from '../../common/Testimonials'
import { ProblemBox } from '../../components/ProblemBox'
import { coreTestimonials } from '../../common/Testimonials'

const GOLD = COLOR_PRIMARY

export default function ProvenApproach() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="The CFP Proven Approach"
                description="The structured six-step methodology Contract Finder Pro clients use to research, find and win UK public sector contracts. From market analysis through to first submission and beyond."
                canonical="/success-network/proven-approach"
            />
            {/* ── Hero ─────────────────────────────────────────────────────────── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`,
                pt: { xs: 8, md: 12 },
                pb: { xs: 8, md: 12 },
                textAlign: 'center',
            }}>
                <Container maxWidth="md">
                    <Chip label="The CFP Method · Success Network" size="small" sx={{
                        mb: 3, backgroundColor: `${GOLD}15`, color: GOLD,
                        border: `1px solid ${GOLD}40`, fontWeight: 700,
                    }} />
                    <Typography variant="h1" sx={{
                        color: 'white',
                        fontSize: { xs: '36px', md: '54px' },
                        lineHeight: 1.15, mb: 3,
                    }}>
                        Find the right tenders.{' '}
                        <Box component="span" sx={{ color: GOLD }}>Write stronger bids.</Box>
                        {' '}Win more public sector work.
                    </Typography>
                    <Typography sx={{
                        color: 'rgba(255,255,255,0.65)',
                        fontSize: { xs: '16px', md: '19px' },
                        lineHeight: 1.75, mb: 5, maxWidth: 640, mx: 'auto',
                    }}>
                        Most suppliers lose in the public sector before they ever write a bid. This is the five-step system our most successful customers follow, combining market research, strategic positioning, relationship investment, compliance preparation and disciplined bidding
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        <FancyGoldButton onClick={() => setDemoOpen(true)} endIcon={<ArrowForwardIcon />}>
                            Book a Discovery Call
                        </FancyGoldButton>
                        <FancyLightButton to="/contactus">
                            Contact Us
                        </FancyLightButton>
                    </Stack>

                    {/* Proof bar */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 2, sm: 4 }}
                        justifyContent="center"
                        alignItems="center"
                        sx={{ mt: 6, pt: 5, borderTop: '1px solid rgba(255,255,255,0.1)' }}
                    >
                        {proofPoints.map(p => (
                            <Box key={p.stat} sx={{ textAlign: 'center' }}>
                                <Typography sx={{ color: GOLD, fontWeight: 800, fontSize: { xs: '22px', md: '26px' }, lineHeight: 1 }}>
                                    {p.stat}
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', mt: 0.5, maxWidth: 160 }}>
                                    {p.label}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} />

            {/* ── The 5 steps ──────────────────────────────────────────────────── */}
            <Box sx={{ backgroundColor: 'white', py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>
                        The Five Step System
                    </Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: { xs: 6, md: 10 }, maxWidth: 580, mx: 'auto' }}>
                        Follow all five and you have the foundation for a sustainable, growing public sector revenue stream
                    </Typography>

                    <Stack spacing={6}>
                        {steps.map((step, i) => (
                            <Box key={step.number} sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: i % 2 === 0 ? 'row' : 'row-reverse' },
                                gap: { xs: 4, md: 6 },
                                alignItems: 'flex-start',
                            }}>
                                {/* Number + icon */}
                                <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{
                                        width: 72, height: 72, borderRadius: 3,
                                        background: `linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <step.Icon sx={{ color: GOLD, fontSize: 32 }} />
                                    </Box>
                                    <Typography sx={{ color: 'rgba(0,0,0,0.35)', fontWeight: 800, fontSize: '13px', letterSpacing: '2px' }}>
                                        STEP {step.number}
                                    </Typography>
                                </Box>

                                {/* Content */}
                                <Box sx={{ flex: 1 }}>
                                    <Chip label={step.label} size="small" sx={{
                                        mb: 1.5, backgroundColor: `${GOLD}15`, color: '#856400',
                                        border: `1px solid ${GOLD}50`, fontWeight: 700, fontSize: '11px',
                                    }} />
                                    <Typography variant="h3" sx={{ fontSize: { xs: '22px', md: '26px' }, mb: 2, lineHeight: 1.3 }}>
                                        {step.heading}
                                    </Typography>
                                    <Typography color="text.secondary" sx={{ fontSize: '15px', lineHeight: 1.8, mb: 3, maxWidth: 560 }}>
                                        {step.body}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {step.bullets.map(b => (
                                            <Grid item xs={12} sm={6} key={b}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                    <CheckCircleIcon sx={{ color: GOLD, fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                                                    <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.55 }}>{b}</Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>

                                {/* Step divider line for non-last items */}
                                {i < steps.length - 1 && (
                                    <Box sx={{
                                        display: { xs: 'none', md: 'none' },
                                    }} />
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Container>
            </Box>

            <WaveDivider top="white" bottom={DARK_800} />

            {/* ── The three products ───────────────────────────────────────────── */}
            <Box sx={{ ...SECTION_DARK }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ color: 'white', textAlign: 'center', mb: 2 }}>
                        Three products, one joined-up workflow
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', mb: 8, maxWidth: 580, mx: 'auto' }}>
                        Each product adds value independently, together they cover the full journey from market scanning to contract submission
                    </Typography>
                    <Grid container spacing={4}>
                        {products.map(p => (
                            <Grid item xs={12} md={4} key={p.name}>
                                <Card sx={{
                                    height: '100%',
                                    background: 'rgba(255,255,255,0.04)',
                                    border: `1px solid ${p.colour}30`,
                                    borderTop: `3px solid ${p.colour}`,
                                    borderRadius: 2,
                                    color: 'white',
                                    transition: 'all 0.2s',
                                    '&:hover': { background: 'rgba(255,255,255,0.07)', borderColor: p.colour },
                                }}>
                                    <CardContent sx={{ p: 3.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{
                                            width: 48, height: 48, borderRadius: 2,
                                            backgroundColor: `${p.colour}20`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                                        }}>
                                            <p.Icon sx={{ color: p.colour, fontSize: 24 }} />
                                        </Box>
                                        <Chip label={p.tag} size="small" sx={{
                                            alignSelf: 'flex-start', mb: 1.5,
                                            backgroundColor: `${p.colour}15`, color: p.colour,
                                            border: `1px solid ${p.colour}40`, fontWeight: 600, fontSize: '11px',
                                        }} />
                                        <Typography fontWeight={700} sx={{ fontSize: '20px', mb: 1.5, color: 'white' }}>
                                            {p.name}
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1.75, flexGrow: 1, mb: 3 }}>
                                            {p.description}
                                        </Typography>
                                        <Box sx={{ borderTop: `1px solid rgba(255,255,255,0.08)`, pt: 2.5 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                                <Typography sx={{ color: GOLD, fontWeight: 700, fontSize: '14px' }}>{p.price}</Typography>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px' }}>{p.forWho}</Typography>
                                            </Stack>
                                            <FancyLightButton to={p.href} endIcon={<ArrowForwardIcon />} fullWidth>
                                                Learn more
                                            </FancyLightButton>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>






            {/* ── Social proof ─────────────────────────────────────────────────── */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="lg">
                    <Box sx={{
                        textAlign: 'center', mb: 2,
                    }} >
                        <Box
                            component="a"
                            href={REVIEWS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                                gap: 0.75, textDecoration: 'none', cursor: 'pointer',
                                borderRadius: 8,
                                px: 5,
                                py: 1,
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                                '&:hover .review-label': { textDecoration: 'underline' },
                            }}
                        >
                            <Box className="star-row" sx={{ display: 'flex', gap: 0.5, transition: 'opacity 0.2s' }}>
                                {[...Array(5)].map((_, i) => (
                                    <StarIcon key={i} sx={{ color: COLOR_PRIMARY, fontSize: 20 }} />
                                ))}
                            </Box>
                            <Typography className="review-label" sx={{ color: DARK_800, fontSize: '13px', transition: 'color 0.2s' }}>
                                5.0 on Reviews.io · 52 reviews · 100% recommend
                                <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', ml: 0.5 }} />
                            </Typography>
                        </Box>
                    </Box>
                    <Grid container spacing={4}>
                        {coreTestimonials.map(t => (
                            <Grid item xs={12} md={4} key={t.name}>
                                <Box sx={{
                                    // backgroundColor: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 3,
                                    p: 3.5,
                                    height: '100%',

                                    display: 'flex',
                                    flexDirection: 'column',
                                }}>
                                    <FormatQuoteIcon sx={{ color: COLOR_PRIMARY, fontSize: 32, mb: 2, opacity: 0.7 }} />
                                    <Typography sx={{ fontSize: '15px', lineHeight: 1.7, flex: 1, mb: 3, fontStyle: 'italic', maxHeight: '320px', overflow: 'auto' }}>
                                        "{t.quote}"
                                    </Typography>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{t.name}</Typography>
                                        <Typography sx={{ fontSize: '13px' }}>✓ Verified Buyer · {t.timeAgo}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
            <ProblemBox items={shouldntWont} quote="You shouldn't have to work this hard to find a tender" title='These are the problems that eat BD time' />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* <Box sx={{ backgroundColor: 'white', py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mb: 1 }}>
                        {[...Array(5)].map((_, i) => <StarIcon key={i} sx={{ color: GOLD, fontSize: 22 }} />)}
                    </Stack>
                    <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 700, mb: 1 }}>5.0 on Reviews.io</Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 6 }}>52 verified reviews · 100% recommend</Typography>
                    <Grid container spacing={3}>
                        {quotes.map(q => (
                            <Grid item xs={12} md={4} key={q.text}>
                                <Box sx={{
                                    p: 3, height: '100%',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    borderRadius: 2, position: 'relative',
                                }}>
                                    <FormatQuoteIcon sx={{ color: `${GOLD}40`, fontSize: 36, mb: 1 }} />
                                    <Typography sx={{ fontSize: '15px', lineHeight: 1.7, mb: 2, fontStyle: 'italic' }}>
                                        "{q.text}"
                                    </Typography>
                                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', fontWeight: 600 }}>
                                        {q.role}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box> */}

            {/* ── Who this is for ──────────────────────────────────────────────── */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Typography variant="h2" sx={{ mb: 2, lineHeight: 1.2 }}>Who this works for</Typography>
                            <Typography color="text.secondary" sx={{ lineHeight: 1.75, mb: 3 }}>
                                CFP is designed for organisations already in or actively entering  UK public sector procurement. The approach delivers results fastest when there is a genuine product-market fit and the capacity to bid consistently.
                            </Typography>
                            <Stack spacing={1.5}>
                                {whoItsFor.map(item => (
                                    <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <CheckCircleIcon sx={{ color: GOLD, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.6 }}>{item}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`,
                                borderRadius: 3, p: { xs: 3, md: 4 },
                            }}>
                                <Typography variant="caption" sx={{
                                    color: GOLD, textTransform: 'uppercase',
                                    letterSpacing: '1px', fontWeight: 700, display: 'block', mb: 2,
                                }}>
                                    The simplest credible next step
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '16px', lineHeight: 1.8, mb: 4 }}>
                                    Start with a free 14-day trial of CFP Core. No commitment, full access to tender search, daily alerts and buyer intelligence. Set your filters once and let the platform run.
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '16px', lineHeight: 1.8, mb: 4 }}>
                                    If you're running a bid team or want to explore Tender Ninja, book a discovery call instead  we'll map out the right configuration for your specific situation.
                                </Typography>
                                <Stack spacing={2}>
                                    <FancyGoldButton to="/register" endIcon={<ArrowForwardIcon />} fullWidth>
                                        Start Free 14-Day Trial
                                    </FancyGoldButton>
                                    <FancyLightButton onClick={() => setDemoOpen(true)} fullWidth>
                                        Book a Discovery Call
                                    </FancyLightButton>
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} inverted />


            {/* <CtaSection onDemoClick={() => setDemoOpen(true)} /> */}
            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
